import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #stockObserver;
  #stockFrame;
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pagination:error', this.#handlePaginationError);
    this.#prioritizeStock();
    this.#stockObserver = new MutationObserver(() => {
      cancelAnimationFrame(this.#stockFrame);
      this.#stockFrame = requestAnimationFrame(() => this.#prioritizeStock());
    });
    this.#stockObserver.observe(this, {childList: true, subtree: true});

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pagination:error', this.#handlePaginationError);
    this.#stockObserver?.disconnect();
    cancelAnimationFrame(this.#stockFrame);
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #prioritizeStock() {
    const grid = this.refs.grid;
    if (!grid) return;
    const cards = [...grid.querySelectorAll(':scope > [ref="cards[]"]')];
    const lastLoaded = Math.max(0, ...cards.map(card => Number(card.dataset.page) || 0));
    const awaitingPages = !this.hasAttribute('data-pagination-error') && this.getAttribute('infinite-scroll') === 'true' &&
      lastLoaded < Number(grid.dataset.lastPage);
    const rank = card => Number(card.querySelector('[data-dc-stock-rank]')?.dataset.dcStockRank) || 0;
    // Keep exhausted cards in the DOM so native pagination still knows its page range.
    // Show this tail only after the remaining pages (and their available products) arrive.
    for (const card of cards) card.hidden = awaitingPages && rank(card) > 0;
    const ordered = cards.map((card, index) => ({card, index, rank: rank(card)}))
      .sort((a, b) => a.rank - b.rank || Number(a.card.dataset.page) - Number(b.card.dataset.page) || a.index - b.index)
      .map(item => item.card);
    if (ordered.some((card, index) => card !== cards[index])) grid.append(...ordered);
  }

  #handlePaginationError = () => this.#prioritizeStock();

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
